import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { RegistrationService } from '../../../core/services/registration.service';
import {
  UserRegistrationRequest,
  UserRegistrationResponse,
} from '../../../core/models/registration/user-registration.model';
import {
  UserOutlined,
  MailOutlined,
  TeamOutlined,
  UserAddOutlined,
  CheckSquareOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Input, Button } from 'antd';
import Swal from 'sweetalert2';
import PublicActionPage from '../components/PublicActionPage';
import PublicFormField from '../components/PublicFormField';

interface FormErrors {
  firstname?: string;
  lastname?: string;
  email?: string;
}

const Register: React.FC = () => {
  const [form, setForm] = useState<UserRegistrationRequest>({
    firstname: '',
    lastname: '',
    email: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [createdName, setCreatedName] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: FormErrors = {};

    if (!form.firstname.trim()) newErrors.firstname = 'First name is required';
    if (!form.lastname.trim()) newErrors.lastname = 'Last name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!isValidEmail(form.email))
      newErrors.email = 'Enter a valid email address';

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const result: UserRegistrationResponse =
        await RegistrationService.Register({
          ...form,
          admin: true,
        });

      setCreatedName(result.firstname || form.firstname);
      setForm({ firstname: '', lastname: '', email: '' });
      setErrors({});

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Account created',
        text: `Welcome ${result.firstname}! Check your email for next steps.`,
        showConfirmButton: false,
        timer: 3500,
        timerProgressBar: true,
        customClass: {
          popup: 'swal2-toast shadow-lg rounded-lg',
        },
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        },
      });
    } catch (error: any) {
      const msg = error.message || 'Registration failed';
      const alreadyExists = msg.toLowerCase().includes('exists');
      if (alreadyExists) {
        setErrors({ email: 'This email is already registered' });
      }
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title: 'Registration failed',
        text: alreadyExists
          ? 'This email is already registered. Try another one.'
          : 'Something went wrong. Please try again.',
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
        customClass: {
          popup: 'swal2-toast shadow-lg rounded-lg',
        },
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicActionPage
      eyebrow="Post Jobs"
      title="Publish roles. Track who applies."
      description="Create a free recruiter workspace to post openings and manage applicants in one place."
      icon={<UserAddOutlined />}
      steps={[
        {
          title: 'Create your account',
          detail: 'Name and work email — takes under a minute.',
        },
        {
          title: 'Check your inbox',
          detail: 'We’ll send sign-in details to get into the workspace.',
        },
        {
          title: 'Post your first job',
          detail: 'Publish a role and start receiving applications.',
        },
      ]}
      asideNote="Built for freelance recruiters and small hiring teams — no credit card for signup."
      formEyebrow="Recruiter signup"
      formTitle="Create your posting account"
      formDescription="We’ll email you how to sign in and start posting."
      footer={
        <p className="jo-body-muted mb-0 text-center text-sm">
          Looking for work instead?{' '}
          <Link to="/jobs" className="public-inline-link">
            Find jobs
          </Link>{' '}
          or set up{' '}
          <Link to="/subscribe" className="public-inline-link">
            Job Alerts
          </Link>
          .
        </p>
      }
    >
      <div className="mx-auto w-full max-w-xl">
        {createdName ? (
          <div
            className="public-result-panel public-result-panel--success"
            role="status"
            aria-live="polite"
          >
            <p className="jo-success-banner mb-2 text-sm font-semibold">
              <CheckSquareOutlined className="mr-2" aria-hidden="true" />
              Welcome, {createdName}. Your account is ready.
            </p>
            <p className="jo-body-muted mb-4 text-sm leading-6">
              Check your inbox for sign-in details, then return here to post
              jobs from your recruiter workspace.
            </p>
            <div className="public-result-actions">
              <Link to="/jobs" className="public-button-primary inline-flex">
                <SearchOutlined aria-hidden="true" />
                Browse the job board
              </Link>
              <Button
                className="public-button-secondary"
                onClick={() => setCreatedName(null)}
              >
                Register another account
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <PublicFormField
                id="post-first-name"
                label="First name"
                error={errors.firstname}
              >
                <Input
                  prefix={<UserOutlined aria-hidden="true" />}
                  name="firstname"
                  placeholder="Ada"
                  value={form.firstname}
                  onChange={handleChange}
                  status={errors.firstname ? 'error' : ''}
                  autoComplete="given-name"
                />
              </PublicFormField>

              <PublicFormField
                id="post-last-name"
                label="Last name"
                error={errors.lastname}
              >
                <Input
                  prefix={<TeamOutlined aria-hidden="true" />}
                  name="lastname"
                  placeholder="Lovelace"
                  value={form.lastname}
                  onChange={handleChange}
                  status={errors.lastname ? 'error' : ''}
                  autoComplete="family-name"
                />
              </PublicFormField>

              <PublicFormField
                id="post-email"
                label="Work email"
                error={errors.email}
                className="sm:col-span-2"
                hint="Use the email where you want account access."
              >
                <Input
                  prefix={<MailOutlined aria-hidden="true" />}
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  value={form.email}
                  onChange={handleChange}
                  status={errors.email ? 'error' : ''}
                  autoComplete="email"
                />
              </PublicFormField>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              className="public-button-primary w-full"
              loading={loading}
            >
              {loading ? 'Creating your account…' : 'Create posting account'}
            </Button>
          </form>
        )}
      </div>
    </PublicActionPage>
  );
};

export default Register;
